import { useMemo } from 'react'
import { useQueries, useQuery } from 'react-query'
import luminaireAPI from '../service/luminaireAPI'
import { Luminaire } from '../types/luminaire.types'
import { Point } from '../../Jouly/types/types'

const FLEET_PAGE_SIZE = 500
const STALE_TIME_MS = 60_000

const lastNumericPoint = (
  points: Point[] | undefined,
): { value: number; time: string | null } | null => {
  if (!points || points.length === 0) return null
  for (let i = points.length - 1; i >= 0; i -= 1) {
    const raw = points[i]?.Value ?? points[i]?.Values?._value
    const n = typeof raw === 'number' ? raw : Number(raw)
    if (Number.isFinite(n)) return { value: n, time: points[i]?.Time ?? null }
  }
  return null
}

const todayBucket = (): string => new Date().toISOString().slice(0, 10)

// Fenêtre d'online glissante : un luminaire qui n'a pas reporté de télémétrie
// dans les 2 dernières heures est considéré hors ligne. Aligné sur le pattern
// utilisé pour les compteurs (cf. useMetersTelemetrySnapshot).
const ONLINE_WINDOW_MS = 2 * 60 * 60 * 1000

export type LuminaireOnlineFlag = 'ON' | 'OFF' | 'NA'

const computeOnlineFlag = (lastSeen: string | null): LuminaireOnlineFlag => {
  if (!lastSeen) return 'NA'
  const t = new Date(lastSeen).getTime()
  if (Number.isNaN(t)) return 'NA'
  return Date.now() - t < ONLINE_WINDOW_MS ? 'ON' : 'OFF'
}

export interface LuminaireFleetTelemetry {
  activeEnergyKwh: number | null
  dimmingLevelPercent: number | null
  // Dernier timestamp Influx vu sur ce luminaire (max sur les métriques fetched).
  // null si aucune métrique n'a jamais été reçue.
  lastSeen: string | null
  // ON  = lastSeen dans les 2h ; OFF = trop ancien ; NA = pas de timestamp.
  onlineFlag: LuminaireOnlineFlag
}

export interface UseLuminaireFleetMetricsOptions {
  tags?: string[]
}

export interface UseLuminaireFleetMetricsResult {
  luminaires: Luminaire[]
  totalCount: number
  totalActiveEnergyKwh: number
  byDevEui: Record<string, LuminaireFleetTelemetry>
  isLoading: boolean
}

export const useLuminaireFleetMetrics = (
  options: UseLuminaireFleetMetricsOptions = {},
): UseLuminaireFleetMetricsResult => {
  const tags = options.tags ?? []
  const tagsKey = tags.join(',')

  const luminairesQuery = useQuery({
    queryKey: ['luminaires-fleet', FLEET_PAGE_SIZE, tagsKey],
    queryFn: () =>
      luminaireAPI.getAllLuminaires(
        0,
        FLEET_PAGE_SIZE,
        undefined,
        tags.length > 0 ? tags : undefined,
      ),
    staleTime: STALE_TIME_MS,
  })

  const luminaires = luminairesQuery.data?.data ?? []
  const totalCount = luminairesQuery.data?.pagination?.total_count ?? 0
  const dateBucket = todayBucket()

  const targets = luminaires.filter((l) => Boolean(l.lum_dev_eui))

  // last=true → backend renvoie la dernière valeur connue avec fallback glissant
  // 30j → 1 an. On omet startDate/stopDate/aggregateWindow pour ne pas désactiver
  // le fallback (cf. luminaireAPI.getLuminaireMetric et fix backend infraAlerts).
  const energyQueries = useQueries(
    targets.map((l) => ({
      queryKey: ['luminaire-fleet-active-energy', l.lum_dev_eui, dateBucket],
      queryFn: () =>
        luminaireAPI.getLuminaireMetric('active_energy', {
          devEui: l.lum_dev_eui,
          startDate: '',
          stopDate: '',
          aggregateWindow: '',
          last: true,
        }),
      staleTime: STALE_TIME_MS,
      enabled: Boolean(l.lum_dev_eui),
    })),
  )

  const dimmingQueries = useQueries(
    targets.map((l) => ({
      queryKey: ['luminaire-fleet-dimming-level', l.lum_dev_eui, dateBucket],
      queryFn: () =>
        luminaireAPI.getLuminaireMetric('dimming_level', {
          devEui: l.lum_dev_eui,
          startDate: '',
          stopDate: '',
          aggregateWindow: '',
          last: true,
        }),
      staleTime: STALE_TIME_MS,
      enabled: Boolean(l.lum_dev_eui),
    })),
  )

  const signature =
    energyQueries.map((q) => q.dataUpdatedAt).join(',') +
    '|' +
    dimmingQueries.map((q) => q.dataUpdatedAt).join(',')

  const byDevEui = useMemo(() => {
    const map: Record<string, LuminaireFleetTelemetry> = {}
    targets.forEach((l, idx) => {
      const energyPoint = lastNumericPoint(energyQueries[idx]?.data as Point[] | undefined)
      const dimmingPoint = lastNumericPoint(dimmingQueries[idx]?.data as Point[] | undefined)
      // Prend le timestamp le plus récent parmi les métriques disponibles : si
      // un device émet l'energy mais plus le dimming (ou inversement), on
      // garde la trace la plus fraîche pour la décision online/offline.
      const candidates = [energyPoint?.time, dimmingPoint?.time].filter(
        (t): t is string => Boolean(t),
      )
      const lastSeen =
        candidates.length === 0
          ? null
          : candidates.reduce((max, t) => (new Date(t).getTime() > new Date(max).getTime() ? t : max))
      map[l.lum_dev_eui] = {
        activeEnergyKwh: energyPoint?.value ?? null,
        dimmingLevelPercent: dimmingPoint?.value ?? null,
        lastSeen,
        onlineFlag: computeOnlineFlag(lastSeen),
      }
    })
    return map
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, targets.length])

  const totalActiveEnergyKwh = useMemo(() => {
    return Object.values(byDevEui).reduce(
      (sum, t) => sum + (t.activeEnergyKwh ?? 0),
      0,
    )
  }, [byDevEui])

  const isLoading =
    luminairesQuery.isLoading ||
    energyQueries.some((q) => q.isLoading) ||
    dimmingQueries.some((q) => q.isLoading)

  return { luminaires, totalCount, totalActiveEnergyKwh, byDevEui, isLoading }
}

export default useLuminaireFleetMetrics
